import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import fetchGradeJobQueue from "./fetchGradeJobQueue";
import { isJobInProgress } from "./jobHelpers";

const EMPTY_JOBS = [];

const isJobFinished = job => job.status === "success" || job.status === "error";

export default function useJobQueueWaiting(matchesJob, onJobsFinished) {
  const jobs = useSelector(
    state => (state.jobsState && state.jobsState.jobs) || EMPTY_JOBS
  );
  const [waiting, setWaiting] = useState(false);
  const previousJobs = useRef(jobs);
  const processedJobIds = useRef(new Set());
  const matchesJobRef = useRef(matchesJob);
  const onJobsFinishedRef = useRef(onJobsFinished);

  matchesJobRef.current = matchesJob;
  onJobsFinishedRef.current = onJobsFinished;

  useEffect(() => {
    const previous = previousJobs.current;
    previousJobs.current = jobs;

    const matches = matchesJobRef.current;
    const hasPending = jobs.some(job => isJobInProgress(job) && matches(job));

    if (!waiting) {
      if (hasPending) {
        setWaiting(true);
      }
      return;
    }

    const completed = jobs.filter(
      job =>
        matches(job) &&
        isJobFinished(job) &&
        !processedJobIds.current.has(job.id) &&
        !previous.some(prev => prev.id === job.id && prev.status === job.status)
    );

    if (completed.length === 0) {
      return;
    }

    completed.forEach(job => processedJobIds.current.add(job.id));

    if (!hasPending) {
      setWaiting(false);
      onJobsFinishedRef.current();
    }
  }, [jobs, waiting]);

  const startWaiting = useCallback(() => {
    setWaiting(true);
    fetchGradeJobQueue();
  }, []);

  return { waiting, startWaiting };
}
